import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { BuildingItemModel } from "../models/buildingItem.model";
import buildingIcon from "../../../icons/building.svg";
import { RouteBuilder } from "../../router/constantes/routes.const";
import { EditIonCardList } from "../../shared/components/edit-ion-card-list.component";
import { useOutdoorAreas } from "../hooks/use-outdoor-areas.hook";
import { useOrganizationCode } from "../../router/hooks/use-organization-code.hook";
import { useLocale } from "../../router/hooks/use-locale.hook";
import { useUserContext } from "../../user/contexts/user.context";
import { Sites } from "../../service-worker/sites";

export interface ZoneListProps {
  siteId: string;
  buildingList: BuildingItemModel[];
  hasOutdoorAreas: boolean;
  siteName: string;
  siteCompositionLabel: string;
  buildingTitleLabel: string;
  outdoorAreasTitleLabel: string;
  numberOfAnnotations: number;
  isExported: boolean;
  numberOfAnnotationsOnArea: number;
  isBuildingList?: boolean;
  isOffline?: boolean;
  refreshTrigger?: number; // Pour forcer le rafraîchissement après synchronisation
}

export const ZoneList: FunctionComponent<ZoneListProps> = ({
  buildingList,
  buildingTitleLabel,
  hasOutdoorAreas,
  outdoorAreasTitleLabel,
  siteCompositionLabel,
  siteId,
  siteName,
  numberOfAnnotations,
  numberOfAnnotationsOnArea,
  isExported,
  isOffline = false,
  refreshTrigger,
}) => {
  const organizationCode = useOrganizationCode();
  const locale = useLocale();
  const { currentOrganization } = useUserContext();
  const [buildingsOfflineStatus, setBuildingsOfflineStatus] = useState<Record<string, boolean>>({});
  
  const routerLinkCallback = useCallback((buildingId: string) => RouteBuilder.building(siteId, buildingId, locale, organizationCode), [siteId, locale, organizationCode]);

  const { outdoorAreas, outdoorAreasRouterLinkCallback } = useOutdoorAreas(hasOutdoorAreas, siteName, siteId, numberOfAnnotations, isExported, numberOfAnnotationsOnArea, locale, organizationCode);
  const [isOutdoorAreaOffline, setIsOutdoorAreaOffline] = useState<boolean>(false);

  // Vérifier le statut offline de chaque bâtiment et de l'espace extérieur
  useEffect(() => {
    async function checkBuildingsOffline() {
      if (!currentOrganization) {
        setBuildingsOfflineStatus({});
        setIsOutdoorAreaOffline(false);
        return;
      }
      
      const statusMap: Record<string, boolean> = {};
      await Promise.all([
        ...buildingList.map(async (building) => {
          const offline = await Sites.isBuildingOffline(currentOrganization.code, siteId, building.id);
          statusMap[building.id] = offline;
        }),
        (async () => {
          if (hasOutdoorAreas) {
            const outdoorOffline = await Sites.isOutdoorAreasOffline(currentOrganization.code, siteId);
            setIsOutdoorAreaOffline(outdoorOffline);
          } else {
            setIsOutdoorAreaOffline(false);
          }
        })()
      ]);
      setBuildingsOfflineStatus(statusMap);
    }
    checkBuildingsOffline();
  }, [currentOrganization, siteId, buildingList, isOffline, hasOutdoorAreas, refreshTrigger]);

  return (
    <EditIonCardList
      title={siteCompositionLabel}
      subtitle={buildingTitleLabel}
      list={buildingList.map((building) => 
        ({ id: building.id, 
          name: building.name, 
          address: building.address,
          icon: buildingIcon, 
          numberOfAnnotations: building.numberOfAnnotations,
          isGraphic: building.isGraphic,
          numberOfExportedBuildings: 0,
          numberOfBuildings: 0,
          isExported: building.isExported,
          isOffline: buildingsOfflineStatus[building.id] || false,
        }))}
      getRouterLink={routerLinkCallback}
      secondSubtitle={outdoorAreasTitleLabel}
      secondList={outdoorAreas.map((area) => ({
        ...area,
        isOffline: isOutdoorAreaOffline,
      }))}
      getSecondRouterLink={outdoorAreasRouterLinkCallback}
      isBuildingList={true}
      showAnnotationsButton={true}
      siteId={siteId}
      numberOfAnnotations={numberOfAnnotations}
      numberOfAnnotationsOnArea={numberOfAnnotationsOnArea}
    />
  );
};
